import { OpError } from '../domain/projectOps';
import * as S from '../domain/sceneOps';
import type { Project } from '../domain/schemas';
import { CHARACTER_PRESETS } from '../scene/characters';
import { SCENE_PRESETS, buildPresetScene } from '../scene/presets';
import type { Command } from './commands';

/** The explicit UI selection scene prompts act on. */
export interface SceneSelection {
  objectIds?: readonly string[];
  layerId?: string | null;
  characterId?: string | null;
}

export interface SceneCommandResult {
  project: Project;
  message: string;
  select?: { objectIds?: string[]; characterId?: string | null; layerId?: string | null };
}

function selectedObjects(project: Project, selection: SceneSelection | undefined) {
  const scene = S.activeSceneOf(project);
  const ids = (selection?.objectIds ?? []).filter((id) => scene.objects.some((o) => o.id === id));
  if (!selection?.objectIds?.length) throw new OpError('No object is selected. Select one on the canvas or in the layer list first.');
  if (ids.length === 0) throw new OpError('The selected object is not in the current scene any more.');
  return { scene, ids, names: scene.objects.filter((o) => ids.includes(o.id)).map((o) => o.name) };
}

const describe = (names: string[]) => (names.length === 1 ? `“${names[0]}”` : `${names.length} objects`);

/** Applies a scene-editor command. Throws OpError (nothing changes) on invalid targets. */
export function applySceneCommand(project: Project, command: Command, selection: SceneSelection | undefined): SceneCommandResult | null {
  switch (command.type) {
    case 'scene.create': {
      if (command.preset === 'blank') {
        const next = S.addScene(project);
        return { project: next, message: 'Created a blank scene.', select: { objectIds: [] } };
      }
      const scene = buildPresetScene(command.preset);
      return {
        project: S.addScene(project, scene),
        message: `Created the “${SCENE_PRESETS[command.preset].label}” scene (${scene.objects.length} editable objects on ${scene.layers.length} layers).`,
        select: { objectIds: [] },
      };
    }
    case 'scene.addObject': {
      const scene = S.activeSceneOf(project);
      const spec: S.NewObject =
        command.shape === 'text'
          ? { type: 'text', text: command.text ?? 'Text', color: command.color }
          : command.shape === 'triangle'
            ? { type: 'polygon', fill: command.color }
            : { type: command.shape, fill: command.color };
      const layerId = selection?.layerId && scene.layers.some((l) => l.id === selection.layerId && !l.locked) ? selection.layerId : undefined;
      const { project: next, id } = S.createObject(project, scene.id, spec, { layerId });
      const o = S.objectOf(S.sceneOf(next, scene.id), id);
      return { project: next, message: `Added ${o.name} to “${scene.name}”.`, select: { objectIds: [id] } };
    }
    case 'object.move': {
      const { scene, ids, names } = selectedObjects(project, selection);
      const parts = [command.dx ? `${Math.abs(command.dx)} px ${command.dx > 0 ? 'right' : 'left'}` : '', command.dy ? `${Math.abs(command.dy)} px ${command.dy > 0 ? 'down' : 'up'}` : ''].filter(Boolean);
      return { project: S.nudgeObjects(project, scene.id, ids, command.dx, command.dy), message: `Moved ${describe(names)} ${parts.join(' and ')}.` };
    }
    case 'object.scale': {
      const { scene, ids, names } = selectedObjects(project, selection);
      return { project: S.scaleObjects(project, scene.id, ids, command.factor), message: `Scaled ${describe(names)} to ${Math.round(command.factor * 100)}%.` };
    }
    case 'object.rotate': {
      const { scene, ids, names } = selectedObjects(project, selection);
      return { project: S.updateObjects(project, scene.id, ids, (o) => ({ rotation: o.rotation + command.degrees })), message: `Rotated ${describe(names)} by ${command.degrees}°.` };
    }
    case 'object.order': {
      const { scene, ids, names } = selectedObjects(project, selection);
      const words = { front: 'to the front', back: 'to the back', forward: 'forward', backward: 'backward' };
      return { project: S.reorderObjects(project, scene.id, ids, command.where), message: `Moved ${describe(names)} ${words[command.where]} of its layer.` };
    }
    case 'object.color': {
      const { scene, ids, names } = selectedObjects(project, selection);
      const objects = scene.objects.filter((o) => ids.includes(o.id));
      if (objects.some((o) => o.type === 'image' || o.type === 'character')) {
        throw new OpError('Images and characters have no single colour. Edit a character’s colours in the character studio.');
      }
      return {
        project: S.updateObjects(project, scene.id, ids, (o) => (o.type === 'text' ? { color: command.color } : { style: { fill: command.color } } as S.ObjectPatch)),
        message: `Coloured ${describe(names)} ${command.color}.`,
      };
    }
    case 'object.visibility': {
      const { scene, ids, names } = selectedObjects(project, selection);
      return { project: S.setVisible(project, scene.id, ids, command.visible), message: `${command.visible ? 'Showing' : 'Hid'} ${describe(names)}.` };
    }
    case 'object.lock': {
      const { scene, ids, names } = selectedObjects(project, selection);
      return { project: S.setLocked(project, scene.id, ids, command.locked), message: `${command.locked ? 'Locked' : 'Unlocked'} ${describe(names)}.` };
    }
    case 'object.duplicate': {
      const { scene, ids, names } = selectedObjects(project, selection);
      const r = S.duplicateObjects(project, scene.id, ids);
      return { project: r.project, message: `Duplicated ${describe(names)}.`, select: { objectIds: r.ids } };
    }
    case 'object.delete': {
      const { scene, ids, names } = selectedObjects(project, selection);
      return { project: S.deleteObjects(project, scene.id, ids), message: `Deleted ${describe(names)}.`, select: { objectIds: [] } };
    }
    case 'character.create': {
      const before = new Set(project.characters.map((c) => c.id));
      const next = S.createCharacter(project, command.preset);
      const created = next.characters.find((c) => !before.has(c.id))!;
      return {
        project: next,
        message: `Created “${created.name}” from the ${CHARACTER_PRESETS[command.preset].label} preset. Select it and say “place the selected character in the scene”, or edit it in the character studio.`,
        select: { characterId: created.id },
      };
    }
    case 'character.place': {
      if (!selection?.characterId) throw new OpError('No character is selected. Select one in the character studio first.');
      const character = S.characterOf(project, selection.characterId);
      const scene = S.activeSceneOf(project);
      const { project: next, id } = S.createObject(project, scene.id, { type: 'character', characterId: character.id });
      return { project: next, message: `Placed “${character.name}” in “${scene.name}”.`, select: { objectIds: [id] } };
    }
    case 'layer.visibility':
    case 'layer.lock': {
      const scene = S.activeSceneOf(project);
      if (!selection?.layerId) throw new OpError('No layer is selected. Select one in the layer panel first.');
      const layer = S.layerOf(scene, selection.layerId);
      if (command.type === 'layer.visibility') {
        return { project: S.updateLayer(project, scene.id, layer.id, { visible: command.visible }), message: `${command.visible ? 'Showing' : 'Hid'} the layer “${layer.name}”.` };
      }
      return { project: S.updateLayer(project, scene.id, layer.id, { locked: command.locked }), message: `${command.locked ? 'Locked' : 'Unlocked'} the layer “${layer.name}”.` };
    }
    default:
      return null;
  }
}
